import { defineStore } from 'pinia'
import axios from 'axios';
import servicesAPI from '@/api/servicesAPI';
import { jwtDecode } from 'jwt-decode';

//Import pinia stores
import { buildVlApiUrl, useWebportalStore } from './webportal';
import { useDebtorStore } from './debtor';
import { useFeedbackStore } from './feedback';
import { useMenuStore } from './menu';
import { useLaundryStore } from './laundry';
import { useOnlineStore } from './online';

//Import models
import { LoginPayload } from '@/models/LoginPayload'


//Setup store
export const useAuthStore = defineStore('auth', {
    state: () => ({
        VLtoken: localStorage.getItem('VlApiToken') || null,
        servicesToken: localStorage.getItem('VlServiceToken') || null,
    }),
    actions: {
        //Authenticate user and get token and set it in localStorage
        async login(payload: LoginPayload) {
            if (!useOnlineStore().online) {
                useFeedbackStore().setTranslatedMessage('danger', 'noInternet');
                return false;
            }

            const webportalStore = useWebportalStore();
            const apiBases = webportalStore.selectedWebportal?.API;
            if (!apiBases?.length) {
                await this.logout();
                return false;
            }

            try {
                // eslint-disable-next-line
                const token = btoa(`${payload.username}:${payload.password}`);

                this.servicesToken = token;
                localStorage.setItem('VlServiceToken', token);

                const results = await Promise.allSettled(
                    apiBases.map((baseUrl) =>
                        axios.post(`${buildVlApiUrl(baseUrl)}/auth/authenticate-webportal`, payload)
                    )
                );

                const successes = results
                    .map((result, index) =>
                        result.status === 'fulfilled' && result.value.data?.data
                            ? { baseUrl: apiBases[index], token: result.value.data.data as string }
                            : null
                    )
                    .filter((s): s is { baseUrl: string; token: string } => s !== null);

                if (successes.length !== 1) {
                    webportalStore.clearAuthenticatedApiUrl();
                    useFeedbackStore().setTranslatedMessage('danger', 'unsuccessLogin');
                    return false;
                }

                webportalStore.setAuthenticatedApiUrl(successes[0].baseUrl);
                this.VLtoken = successes[0].token;
                localStorage.setItem('VlApiToken', successes[0].token);

                // Login webportal service to support previous VL App versions.
                // TODO: Can be removed when all laundries with a webportal have been updated.
                const serviceUrl = webportalStore.selectedWebportal?.ServiceUrl;
                if (serviceUrl) {
                    try {
                        await servicesAPI.post(`${serviceUrl}/Greet`, {}, {
                            headers: { 'Authorization': `Basic ${token}` }
                        });
                    } catch {
                        // Optional webportal calls should not undo a successful API login
                    }
                }

                useFeedbackStore().setTranslatedMessage('success', 'successLogin');

                await useLaundryStore().getLaundry();
                await useMenuStore().getMenuItems();
                await useDebtorStore().getDebtors();

                return true;
            } catch (error: any) {
                webportalStore.clearAuthenticatedApiUrl();
                useFeedbackStore().setTranslatedMessage('danger', 'unsuccessLogin');
                return false;
            }
        },

        
        async resetPassword(payload: {username: string, email: string}) {
            if (!useOnlineStore().online) {
                useFeedbackStore().setTranslatedMessage('danger', 'noInternet');
                return false;
            }

            const webportalStore = useWebportalStore();
            const apiBases = webportalStore.selectedWebportal?.API;
            if (!apiBases?.length) {
                await this.logout();
                return false;
            }

            const requestPayload = {
                username: payload.username,
                email: payload.email,
                webportalName: webportalStore.selectedWebportal?.Naam
            };

            try {
                const results = await Promise.allSettled(
                    apiBases.map((baseUrl) =>
                        axios.post(`${buildVlApiUrl(baseUrl)}/auth/webportal/reset-password`, requestPayload)
                    )
                );

                const isNotFound = (result: PromiseSettledResult<unknown>) =>
                    result.status === 'rejected' &&
                    axios.isAxiosError(result.reason) &&
                    result.reason.response?.status === 404;

                const successes = results.filter(
                    (result) => result.status === 'fulfilled' && !!result.value.data?.data
                );

                if (successes.length > 0) {
                    useFeedbackStore().setTranslatedMessage('success', 'successResetPasswordRequested');
                }

                if (successes.length === results.length) {
                    return true;
                }

                // Fallback for older API versions without the reset-password endpoint.
                // TODO: Can be removed when all laundries with a webportal have been updated.
                if (results.some(isNotFound)) {
                    return this.resetPasswordViaWebportalService(payload);
                }

                useFeedbackStore().setTranslatedMessage('danger', 'basicError');
                return false;
            } catch {
                useFeedbackStore().setTranslatedMessage('danger', 'basicError');
                return false;
            }
        },

        // Reset password via legacy webportal service.
        // TODO: Can be removed when all laundries with a webportal have been updated.
        async resetPasswordViaWebportalService(payload: {username: string, email: string}) {
            if (!useWebportalStore()?.selectedWebportal?.ServiceUrl) {
                await this.logout();
                return false;
            }

            try {
                const token = btoa(`${payload.username}:${" "}`);

                this.servicesToken = token;
                const result = await servicesAPI.post(`${useWebportalStore()?.selectedWebportal?.ServiceUrl ?? ''}/GetWachtwoordVergeten`, {
                    ...payload,
                    api: 2
                }, {
                    headers: {
                        'Authorization': `Basic ${token}`
                    }
                });
                if (result.data.Successful) {
                    useFeedbackStore().setTranslatedMessage('success', 'successResetPasswordSent');
                    return true;
                } else {
                    useFeedbackStore().setTranslatedMessage('danger', 'unsuccessResetPasswordSent');
                    return false;
                }
            } catch {
                useFeedbackStore().setTranslatedMessage('danger', 'unsuccessResetPasswordSent');
                return false;
            }
        },

        async changePassword(payload: { code: string; password: string }) {
            if (!useOnlineStore().online) {
                useFeedbackStore().setTranslatedMessage('danger', 'noInternet');
                return false;
            }

            const webportalStore = useWebportalStore();
            const apiBases = webportalStore.selectedWebportal?.API;
            if (!apiBases?.length) {
                useFeedbackStore().setTranslatedMessage('danger', 'basicError');
                return false;
            }

            try {
                const results = await Promise.allSettled(
                    apiBases.map((baseUrl) =>
                        axios.post(`${buildVlApiUrl(baseUrl)}/auth/webportal/change-password`, payload)
                    )
                );

                const successes = results.filter(
                    (result) => result.status === 'fulfilled' && !!result.value.data?.data
                );

                if (successes.length > 0) {
                    useFeedbackStore().setTranslatedMessage('success', 'successPasswordChange');
                    return true;
                }

                const badRequest = results.find(
                    (result) =>
                        result.status === 'rejected' &&
                        axios.isAxiosError(result.reason) &&
                        result.reason.response?.status === 400
                );

                if (badRequest?.status === 'rejected' && axios.isAxiosError(badRequest.reason)) {
                    const message = badRequest.reason.response?.data?.message;
                    if (message) {
                        useFeedbackStore().setMessage('danger', message);
                        return false;
                    }
                }

                useFeedbackStore().setTranslatedMessage('danger', 'unsuccessPasswordChange');
                return false;
            } catch {
                useFeedbackStore().setTranslatedMessage('danger', 'unsuccessPasswordChange');
                return false;
            }
        },

        //Delete tokens from localStorage
        async logout() {
            localStorage.removeItem('VlApiToken');
            localStorage.removeItem('VlServiceToken');
            localStorage.removeItem('authenticatedApiUrl');
            localStorage.removeItem('laundry');
            localStorage.removeItem('route');
            localStorage.removeItem('menuAutoOpened'); // Clear menu auto-open flag
            this.VLtoken = null;
            this.servicesToken = null;
            useWebportalStore().clearAuthenticatedApiUrl();
        }
    },
    getters: {
        dragerLogin: (state) => {
            return !!(state.VLtoken && (jwtDecode(state.VLtoken) as any).Drager)
        },
        drager: (state) => {
            return (state.VLtoken ? (jwtDecode(state.VLtoken) as any).Drager : null)
        }
    }
})